import { z } from 'zod';
import { identifierSchema, nameSchema, serviceTypeSchema, uuidSchema } from '../common';
import { pageSchema } from './pagination';

/**
 * The infrastructure layer (A-T3): resources and service bindings. Viewing needs
 * `infrastructure.view`, changes need `infrastructure.manage` (Admin only). Shared vs dedicated is never stored: `usage`
 * is derived from the service links on every read (A-T2, A-S8). `integrationId` is not
 * accepted until an integration exists (A-T5, D-4).
 */
const externalRefSchema = z.string().trim().min(1).max(500);

export const resourceCreateSchema = z.strictObject({
  kind: identifierSchema,
  name: nameSchema,
  externalRef: externalRefSchema.nullable().optional(),
  parentResourceId: uuidSchema.nullable().optional(),
});
export type ResourceCreate = z.infer<typeof resourceCreateSchema>;

export const resourceUpdateSchema = z
  .strictObject({
    name: nameSchema.optional(),
    externalRef: externalRefSchema.nullable().optional(),
    parentResourceId: uuidSchema.nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'no fields to update' });
export type ResourceUpdate = z.infer<typeof resourceUpdateSchema>;

export const resourceParamsSchema = z.object({ resourceId: uuidSchema });

export const resourceUsageSchema = z.object({
  /** Distinct services linked to this resource. More than one means the resource is shared. */
  linkedServiceCount: z.number().int(),
});

export const resourceSchema = z.object({
  id: uuidSchema,
  kind: z.string(),
  name: z.string(),
  externalRef: z.string().nullable(),
  parentResourceId: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  usage: resourceUsageSchema,
});
export type Resource = z.infer<typeof resourceSchema>;

export const resourceDetailSchema = resourceSchema.extend({
  children: z.array(z.object({ id: uuidSchema, kind: z.string(), name: z.string() })),
  linkedServices: z.array(
    z.object({
      bindingId: uuidSchema,
      serviceId: uuidSchema,
      serviceType: serviceTypeSchema,
      role: z.string(),
      scopeRef: z.string().nullable(),
    }),
  ),
});
export type ResourceDetail = z.infer<typeof resourceDetailSchema>;

export const resourcePageSchema = pageSchema(resourceSchema);

// --- Service bindings (which resources deliver a service) -------------------------------------

export const bindingCreateSchema = z.strictObject({
  resourceId: uuidSchema,
  role: identifierSchema,
  scopeRef: z.string().trim().min(1).max(500).nullable().optional(),
});
export type BindingCreate = z.infer<typeof bindingCreateSchema>;

export const bindingParamsSchema = z.object({ serviceId: uuidSchema, bindingId: uuidSchema });

export const bindingSchema = z.object({
  id: uuidSchema,
  resource: z.object({ id: uuidSchema, kind: z.string(), name: z.string() }),
  role: z.string(),
  scopeRef: z.string().nullable(),
  createdAt: z.string(),
});
export type Binding = z.infer<typeof bindingSchema>;

export const bindingListSchema = z.object({ items: z.array(bindingSchema) });
